/**
 * Pull-test installation depths are stored as **decimal US survey feet** on the API
 * (`pushedToDepthFt`, `actualEmbedmentDepthFt`). Operators enter **feet + inches**; these helpers convert.
 */

export interface FtInParts {
  ft: number;
  inch: number;
}

/** Convert stored decimal feet into whole feet + inches (inch may be fractional). */
export function decimalFeetToFtInParts(dec: number): FtInParts {
  if (!Number.isFinite(dec) || dec < 0) {
    return { ft: 0, inch: 0 };
  }
  const totalInches = dec * 12;
  const ft = Math.floor(totalInches / 12 + 1e-9);
  let inch = totalInches - ft * 12;
  inch = Math.round(inch * 1_000_000) / 1_000_000;
  if (inch >= 12 - 1e-6) {
    return { ft: ft + 1, inch: 0 };
  }
  return { ft, inch };
}

/** Combine feet and inch parts into one decimal-feet value. */
export function ftInPartsToDecimalFeet(ft: number, inch: number): number {
  if (!Number.isFinite(ft) || !Number.isFinite(inch) || ft < 0 || inch < 0 || inch >= 12) {
    return Number.NaN;
  }
  return ft + inch / 12;
}

/** Human-readable label for review / detail views (e.g. `5 ft 6 in`). */
export function formatFeetInchesFromDecimalFeet(dec: number): string {
  const { ft, inch } = decimalFeetToFtInParts(dec);
  if (!Number.isFinite(dec) || dec < 0) {
    return '—';
  }
  if (inch <= 1e-6) {
    return `${ft} ft`;
  }
  const inchStr =
    Math.abs(inch - Math.round(inch)) < 1e-6 ? String(Math.round(inch)) : inch.toFixed(2).replace(/\.?0+$/, '');
  return `${ft} ft ${inchStr} in`;
}
